import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuthTokens, getAccessToken, saveTokenPair } from "../../api/client";
import { login, whoAmI } from "../../api/services/clinic/account";
import { initializePrinterDefaultsForRole } from "../../api/services/devices/printer";
import type { UserProfile } from "../../api/services/clinic/account.types";
import { useUser } from "../../contexts/UserContext";
import { normalizePassword, normalizeUsername } from "../../utils/authInput";
import LoginForm from "./loginForm.jsx";
import StaffRegisterLayout from "../staff_register/StaffRegisterLayout";
import { handleUserCurrentClinicBranch } from "../../utils/authRedirect";

const INVALID_CREDENTIALS_MESSAGE = "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้องโปรดลองใหม่อีกครั้ง";
const AUTOFILL_POLL_INTERVAL_MS = 250;
const AUTOFILL_POLL_MAX_ATTEMPTS = 20;

function buildUserState(currentUser: UserProfile) {
  return {
    id: currentUser?.id ?? null,
    full_name: currentUser?.full_name ?? "",
    role: currentUser?.role ?? "",
    accessible_pages: Array.isArray(currentUser?.accessible_pages)
      ? currentUser.accessible_pages
      : [],
    current_clinic_branch: currentUser?.current_clinic_branch,
    clinic_branches: currentUser?.clinic_branches || [],
  };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setRequiredAction } = useUser();
  const formRef = useRef(null);
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const usernameValueRef = useRef("");
  const passwordValueRef = useRef("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingExistingSession, setIsCheckingExistingSession] = useState(true);

  const syncAuthenticatedUser = useCallback(async () => {
    const currentUser = await whoAmI();
    initializePrinterDefaultsForRole(currentUser?.role);
    const branch = await handleUserCurrentClinicBranch(currentUser, navigate);
    const nextUserState = buildUserState(currentUser);
    setUser({
      ...nextUserState,
      current_clinic_branch: branch ?? nextUserState.current_clinic_branch ?? null,
    });
    setRequiredAction(currentUser?.required_action ?? null);
  }, [navigate, setRequiredAction, setUser]);

  const readCredentialsFromForm = useCallback(() => {
    const usernameField = usernameInputRef.current
      ?? formRef.current?.elements.namedItem("username");
    const passwordField = passwordInputRef.current
      ?? formRef.current?.elements.namedItem("password");
    const nextUsername = typeof usernameField?.value === "string"
      ? normalizeUsername(usernameField.value)
      : "";
    const nextPassword = typeof passwordField?.value === "string"
      ? normalizePassword(passwordField.value)
      : "";

    return {
      username: nextUsername,
      password: nextPassword,
    };
  }, []);

  const syncAutofilledCredentials = useCallback(() => {
    const {
      username: autofilledUsername,
      password: autofilledPassword,
    } = readCredentialsFromForm();
    const currentUsername = usernameValueRef.current;
    const currentPassword = passwordValueRef.current;

    if (!currentUsername.trim() && autofilledUsername && autofilledUsername !== currentUsername) {
      setUsername(autofilledUsername);
    }

    if (!currentPassword.trim() && autofilledPassword && autofilledPassword !== currentPassword) {
      setPassword(autofilledPassword);
    }
  }, [readCredentialsFromForm]);

  useEffect(() => {
    usernameValueRef.current = username;
    passwordValueRef.current = password;
  }, [password, username]);

  const handleCredentialFieldEvent = useCallback((event) => {
    if (event?.type === "animationstart" && event.animationName !== "loginAutofillDetected") {
      return;
    }

    window.setTimeout(() => {
      syncAutofilledCredentials();
    }, 0);
  }, [syncAutofilledCredentials]);

  useEffect(() => {
    let isActive = true;

    const redirectAuthenticatedUser = async () => {
      if (!getAccessToken()) {
        if (isActive) {
          setIsCheckingExistingSession(false);
        }
        return;
      }

      try {
        await syncAuthenticatedUser();
        if (!isActive) {
          return;
        }
      } catch {
        if (!isActive) {
          return;
        }

        clearAuthTokens();
        setIsCheckingExistingSession(false);
      }
    };

    redirectAuthenticatedUser();

    return () => {
      isActive = false;
    };
  }, [navigate, syncAuthenticatedUser]);

  useEffect(() => {
    if (username.trim() && password.trim()) {
      return undefined;
    }

    syncAutofilledCredentials();

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      syncAutofilledCredentials();

      if (attempts >= AUTOFILL_POLL_MAX_ATTEMPTS) {
        window.clearInterval(intervalId);
      }
    }, AUTOFILL_POLL_INTERVAL_MS);

    const handleWindowFocus = () => {
      syncAutofilledCredentials();
    };

    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("pageshow", handleWindowFocus);
    document.addEventListener("visibilitychange", handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("pageshow", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleWindowFocus);
    };
  }, [password, syncAutofilledCredentials, username]);

  useEffect(() => {
    const usernameField = usernameInputRef.current;
    const passwordField = passwordInputRef.current;

    if (!usernameField && !passwordField) {
      return undefined;
    }

    const handleNativeCredentialEvent = () => {
      window.setTimeout(() => {
        syncAutofilledCredentials();
      }, 0);
    };

    const credentialFields = [usernameField, passwordField].filter(Boolean);
    const eventNames = ["input", "change", "keydown", "keyup", "animationstart"];

    credentialFields.forEach((field) => {
      eventNames.forEach((eventName) => {
        field.addEventListener(eventName, handleNativeCredentialEvent);
      });
    });

    handleNativeCredentialEvent();

    return () => {
      credentialFields.forEach((field) => {
        eventNames.forEach((eventName) => {
          field.removeEventListener(eventName, handleNativeCredentialEvent);
        });
      });
    };
  }, [syncAutofilledCredentials]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let nextUsername = username.trim() ? username : "";
    let nextPassword = password.trim() ? password : "";

    if (!nextUsername || !nextPassword) {
      const credentialsFromForm = readCredentialsFromForm();
      nextUsername = nextUsername || credentialsFromForm.username;
      nextPassword = nextPassword || credentialsFromForm.password;
    }

    if (!nextUsername || !nextPassword) {
      setErrorMessage("กรุณากรอกชื่อผู้ใช้และรหัสผ่าน");
      return;
    }

    setUsername(nextUsername);
    setPassword(nextPassword);

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const response = await login(nextUsername, nextPassword);

      if (!response?.access) {
        setErrorMessage("ไม่สามารถเข้าสู่ระบบได้");
        return;
      }

      saveTokenPair(response.access, response.refresh);

      await syncAuthenticatedUser();
    } catch (err) {
      const error = err as { status?: number; detail?: string };
      setErrorMessage(
        error?.status === 401
          ? INVALID_CREDENTIALS_MESSAGE
          : error?.detail || INVALID_CREDENTIALS_MESSAGE
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StaffRegisterLayout
      widget={(
        <LoginForm
          formRef={formRef}
          usernameInputRef={usernameInputRef}
          passwordInputRef={passwordInputRef}
          username={username}
          password={password}
          errorMessage={errorMessage}
          isSubmitting={isSubmitting || isCheckingExistingSession}
          onCredentialFieldEvent={handleCredentialFieldEvent}
          onUsernameChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setUsername(normalizeUsername(event.target.value));
            setErrorMessage("");
          }}
          onPasswordChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(normalizePassword(event.target.value));
            setErrorMessage("");
          }}
          onSubmit={handleSubmit}
        />
      )}
    />
  );
}
